/**
 * The Lion & Crown Resort, blocked out.
 *
 * Every number here is FEET. `src/world/build.js` converts once, on the way to
 * geometry and to collision, and nothing downstream sees a foot again. Feet
 * because this is a building: the people who specify doorways, ceiling heights
 * and banquet table spacing specify them in feet, and a blockout authored in
 * metres is a blockout authored in guesses.
 *
 * The plan, looking down, +X east and +Z south:
 *
 *      z=-76                ┌──────────────┐
 *                           │  DOCK APRON  │            (outside, no roof)
 *      z=-52        ┌───────┴──────────────┤──────┬───────┬────┬────────────┐
 *      z=-44 ┌──────┤     LOADING DOCK     │ MGR  │ BREAK │ALC │  BANQUET   │
 *            │KITCHN│    ╷            ╷    │      │       │    │  STORAGE   │
 *      z= -2 ├──────┤    ╷            └────┴──────┴───────┴────┴────────────┤
 *            │      └────╷   S E R V I C E   C O R R I D O R                │
 *      z= 10 ├──────┐    ╷  ┌────────────────────────┬─────────────────────┤
 *            │      │ M  ╷  │                        │                     │
 *            │ ENG  │ A  ╷  │                        │                     │
 *      z= 44 └──────┤ I  ╷  │       BALLROOM         │        LOBBY        │
 *                   │ N  ╷  │                        │                     │
 *                   │    ╷  │                        ├─────────────────────┘
 *                   │ R  ╷  │                        │
 *                   │ U  ╷  │                        │
 *      z=102        └─N──┴──┴────────────────────────┘
 *            x=-62 x=-8  x=6                       x=106                x=196
 *
 * And the site round it, after the real property's aerial view and compressed,
 * north up:
 *
 *   north        the main building, a Y of wings (drawn), its drive looping
 *                in from the west; guest parking west and south-west (drawn)
 *   the lot      its gate west onto the West Drive, which runs south down
 *                the side of the building
 *   the road     west off the drive behind the back of house, south past
 *                Burro Wash (east of it) and the snake, west past the
 *                gatehouse, to the Caytons: half a minute in the golf cart
 *   the lawns    the South Lane behind the ballroom to the Sage Lawn (south)
 *                and the Breeze Lawn (south-east); the Wren Lawn east of the
 *                lobby
 *   the back     the service drive, from the Breeze Lawn up the east side and
 *                along the back of house to the apron's east gate
 *   east         the Sunstone Pool and the Saltbush, across the service drive
 *
 * The west end is where the other departments live, and it is deliberately the
 * far end. Banquet storage is east, the ballroom is middle, and the people who
 * are holding the thing you are short of are two hundred feet the other way.
 * Scarcity has to cost minutes or it is a menu.
 *
 * Three rules the layout is built on:
 *
 *   1. One connected space, no doors that load. Every room opens off the one
 *      corridor, and the corridor opens onto the ballroom and — fatally — onto
 *      the lobby. The whole game is the tension between those last two.
 *   2. Back of house is tight and low, front of house is not. Nine foot
 *      ceilings and twelve foot corridors in the service side, a twenty-four
 *      foot ballroom and a twenty foot lobby in the public side. You should be
 *      able to feel which side of the wall you are on with the camera pointed
 *      at the floor.
 *   3. One run is long and straight and nothing interrupts it. The main
 *      service run starts on the loading dock floor at z=-42, crosses the
 *      service corridor at an open junction, and ends at its own south wall at
 *      z=102 — a hundred and forty-four feet with not one door in it, with the
 *      ballroom's service entrance in the last twelve. The service corridor,
 *      which used to be the spine, is now the branch that crosses it.
 *
 * What the long run costs, and it is the point of it: a straight with no door
 * in it has no corner to come round and nothing to step behind. It is the most
 * exposed hundred and forty-four feet on the property, there is no hollow
 * anywhere along it, and the Banquet Manager walks it end to end all shift.
 * You can cross the whole building on the cart, at twenty miles an hour, in
 * full view of the one person who writes you up.
 *
 * Shared walls are authored once. The dock's east wall is also the manager's
 * office west wall; the break room's east wall is also the alcove's west wall.
 * Doubling them would be invisible and would double the collision boxes in the
 * busiest part of the map.
 */

// --- palette -----------------------------------------------------------------
// Flat colours only. The job of the palette is to tell you, in one glance and
// with no signage, whether you are somewhere you are allowed to be.

const CONCRETE = 0x6b6f77;
/*
 * What a floor is made of, and now also what it sounds like.
 *
 * `floor` is a word per room, alongside the colour. Nothing in the rules reads
 * it — no distance, no sightline and no write-up knows a floor has a surface —
 * it is there so the audio layer can be handed a plain string and told to make
 * a noise. Which is the whole reason the building announces you on the marble
 * and does not on the ballroom carpet: the floors have been saying that in
 * colour since the blockout and now they say it out loud.
 */
const CONCRETE_FLOOR = 0x4e5259;
const CONCRETE_CEIL = 0x5f6470;
const DOCK_FLOOR = 0x44474d;
const ASPHALT = 0x35383d;
const BREAK_FLOOR = 0x5c5445;
const OFFICE_FLOOR = 0x4a4f5c;
const STORE_FLOOR = 0x4a4d52;
const KITCHEN_FLOOR = 0x4f565c;
const SHOP_FLOOR = 0x4b4a45;

const BALLROOM_FLOOR = 0x6d5f50;
const BALLROOM_WALL = 0x8b8071;
const BALLROOM_CEIL = 0x7c7061;

/** The tell. Marble is the brightest thing in the game and it is not for you. */
const MARBLE_FLOOR = 0xb0aca3;
const MARBLE_WALL = 0xc6c0b6;
const MARBLE_CEIL = 0xd2cdc3;

export const PALETTE = {
  CONCRETE,
  CONCRETE_FLOOR,
  BALLROOM_FLOOR,
  MARBLE_FLOOR,
};

export const LEVEL = {
  name: 'lion-and-crown',

  // The employee entrance, in the break room, facing the corridor. You start
  // every shift where you would actually start one.
  spawn: { x: 76, z: -26, yaw: 0 },

  rooms: [
    {
      id: 'corridor',
      name: 'Service Corridor',
      zone: 'boh',
      rect: [-62, -2, 184, 10],
      ceiling: 9,
      floorColor: CONCRETE_FLOOR,
      floor: 'concrete',
      ceilingColor: CONCRETE_CEIL,
    },
    /*
     * The main service run.
     *
     * Fourteen feet wide — two more than the corridor, because this is the one
     * everything comes down — and nine feet like the rest of back of house. It
     * starts on the loading dock floor, crosses the service corridor at an open
     * junction with no door either side of it, and dies at the ballroom's
     * service entrance ninety-two feet later.
     *
     * Nothing is parked in it and nothing is stored in it. That is not tidiness,
     * it is the deal: a hundred and forty-four feet with no corner and no
     * doorway is a hundred and forty-four feet you can ride, and a hundred and
     * forty-four feet anybody standing in it can watch you ride.
     */
    {
      id: 'run',
      name: 'Main Service Run',
      zone: 'boh',
      rect: [-8, 10, 6, 102],
      ceiling: 9,
      floorColor: CONCRETE_FLOOR,
      floor: 'concrete',
      ceilingColor: CONCRETE_CEIL,
    },
    {
      id: 'kitchen',
      name: 'Main Kitchen',
      zone: 'boh',
      rect: [-56, -44, -8, -2],
      ceiling: 12,
      floorColor: KITCHEN_FLOOR,
      floor: 'tile',
      ceilingColor: CONCRETE_CEIL,
    },
    {
      id: 'shop',
      name: 'Engineering Shop',
      zone: 'boh',
      rect: [-56, 10, -8, 44],
      ceiling: 11,
      floorColor: SHOP_FLOOR,
      floor: 'concrete',
      ceilingColor: CONCRETE_CEIL,
    },
    /*
     * The loading dock, which is now also the top of the main run.
     *
     * It used to stop at x=0, with eight feet of nothing between it and the
     * kitchen's east wall. Carrying it west to x=-8 closes that gap and puts
     * the run's head on the dock floor, at the foot of the platform, where a
     * pallet actually lands.
     */
    {
      id: 'dock',
      name: 'Loading Dock',
      zone: 'boh',
      rect: [-8, -52, 48, -2],
      ceiling: 14,
      floorColor: DOCK_FLOOR,
      floor: 'concrete',
      ceilingColor: CONCRETE_CEIL,
    },
    {
      id: 'apron',
      name: 'Dock Apron',
      zone: 'outside',
      rect: [0, -76, 48, -52],
      // No ceiling: this is the one place you can see sky, which is most of
      // why it feels like somewhere you should not be standing on the clock.
      ceiling: 0,
      floorColor: ASPHALT,
      floor: 'asphalt',
    },
    /*
     * The staff lot, and what is past it.
     *
     * The property does not end at the dock wall, and the moment climbing and
     * wildlife existed it was strange that it did. The lot is asphalt with
     * nobody on it — no route runs through it, which is not an oversight, it is
     * the whole of what the lot is for. It is the quietest place on the
     * property and the only way to the Caytons.
     */
    {
      id: 'parking',
      name: 'Staff Lot',
      zone: 'outside',
      rect: [-64, -112, 0, -52],
      ceiling: 0,
      floorColor: ASPHALT,
      floor: 'asphalt',
    },
    /*
     * --- the site ------------------------------------------------------------
     *
     * Everything outside the walls, laid out after the real property's aerial
     * view and compressed (see the site plan at the top of this file). Each
     * piece is a rectangle, fenced or walled on every side it does not share
     * with the next, because a floor with nothing at its edge is somewhere to
     * fall off the world.
     *
     * The West Drive: from the staff lot's west gate, south down the side of
     * the building past the guest parking, to where the cart road leaves for
     * the Caytons. The lane to the lawns turns off it behind the ballroom.
     */
    {
      id: 'drive',
      name: 'West Drive',
      zone: 'outside',
      rect: [-88, -112, -64, 126],
      ceiling: 0,
      floorColor: ASPHALT,
      floor: 'asphalt',
    },
    /*
     * The South Lane, along the back of the ballroom from the drive to the
     * lawns. It is how a strike gets from the lawns to the dock, round the
     * outside of the building: there is no way through it.
     */
    {
      id: 'lane',
      name: 'South Lane',
      zone: 'outside',
      rect: [-64, 102, 124, 126],
      ceiling: 0,
      floorColor: ASPHALT,
      floor: 'asphalt',
    },
    /*
     * The Breeze Lawn, south-east of the ballroom, the big one.
     *
     * Where the outdoor functions are held, and so where they are struck: tables
     * folded, chairs stacked, all of it on carts and round to the dock. Grass,
     * open sky, and nothing on it between functions but whatever was left from
     * the last one. The name is the lawn's own; the reference property's lawns
     * are not named anywhere in this game (see `docs/property-reference.md`).
     *
     * Two gates: one west onto the lane, which is the way from the staff lot,
     * and one north-east onto the service drive, which is the short way to the
     * dock. The only way from it into the building is the apron and the dock
     * stairs. A golf cart can make all of that but the stairs.
     */
    {
      id: 'lawn',
      name: 'Breeze Lawn',
      zone: 'outside',
      rect: [124, 78, 280, 200],
      ceiling: 0,
      floorColor: 0x55703f,
      floor: 'grass',
    },
    // The smaller lawn directly south of the ballroom, through a gate off the lane.
    {
      id: 'sage',
      name: 'Sage Lawn',
      zone: 'outside',
      rect: [16, 126, 96, 190],
      ceiling: 0,
      floorColor: 0x55703f,
      floor: 'grass',
    },
    // And the small one east of the lobby, between the Breeze Lawn and the
    // service drive.
    {
      id: 'wren',
      name: 'Wren Lawn',
      zone: 'outside',
      rect: [196, 10, 252, 78],
      ceiling: 0,
      floorColor: 0x55703f,
      floor: 'grass',
    },
    /*
     * The service drive, behind the building: from the Breeze Lawn's north-east
     * gate up past the lobby, and west along the back of house to a gate in the
     * apron's east wall. How a strike's carts get to the dock without going
     * round the whole building, and how a golf cart gets to the pool. Two
     * rooms because a room is one rectangle.
     */
    {
      id: 'service-e',
      name: 'Service Drive',
      zone: 'outside',
      rect: [252, -76, 276, 78],
      ceiling: 0,
      floorColor: ASPHALT,
      floor: 'asphalt',
    },
    {
      id: 'service-n',
      name: 'Service Drive',
      zone: 'outside',
      rect: [48, -76, 252, -54],
      ceiling: 0,
      floorColor: ASPHALT,
      floor: 'asphalt',
    },
    /*
     * The Sunstone Pool, east of the hotel across the service drive, with the
     * Saltbush beside it.
     *
     * A deck round a pool, and the pool is a hole: its floor is three and a
     * half feet down, so anybody who walks off the edge is in the water, and
     * anybody in the water climbs out the way they climb onto anything. The
     * deck is four rectangles round the water because a room is one
     * rectangle. The south deck, the widest, is the one events are set on.
     */
    {
      id: 'pool',
      name: 'Sunstone Pool',
      zone: 'outside',
      rect: [276, -40, 410, 10],
      ceiling: 0,
      floorColor: 0xa89880,
      floor: 'concrete',
    },
    {
      id: 'pool-n',
      name: 'Sunstone Pool',
      zone: 'outside',
      rect: [276, -120, 410, -80],
      ceiling: 0,
      floorColor: 0xa89880,
      floor: 'concrete',
    },
    {
      id: 'pool-w',
      name: 'Sunstone Pool',
      zone: 'outside',
      rect: [276, -80, 300, -40],
      ceiling: 0,
      floorColor: 0xa89880,
      floor: 'concrete',
    },
    {
      id: 'pool-e',
      name: 'Sunstone Pool',
      zone: 'outside',
      rect: [370, -80, 410, -40],
      ceiling: 0,
      floorColor: 0xa89880,
      floor: 'concrete',
    },
    {
      id: 'pool-water',
      name: 'Sunstone Pool',
      zone: 'outside',
      rect: [300, -80, 370, -40],
      base: -3.5,
      ceiling: 0,
      floorColor: 0x3f8fa8,
      floor: 'water',
    },
    /*
     * The cart road, from the West Drive to the Caytons: west off the drive
     * behind the back of house, south past the wash, and west again past the
     * gatehouse. Three straights and two bends, and the turn off the drive,
     * because a road through the desert goes round things. Compressed from
     * the real one to half a minute in the golf cart, which is half an hour of
     * the clock: a minute and a half was an hour and a half, which is a shift.
     */
    {
      id: 'road-1',
      name: 'Cart Road',
      zone: 'outside',
      rect: [-148, -36, -88, -12],
      ceiling: 0,
      floorColor: ASPHALT,
      floor: 'asphalt',
    },
    {
      id: 'road-2',
      name: 'Cart Road',
      zone: 'outside',
      rect: [-172, -36, -148, 64],
      ceiling: 0,
      floorColor: ASPHALT,
      floor: 'asphalt',
    },
    {
      id: 'road-3',
      name: 'Cart Road',
      zone: 'outside',
      rect: [-222, 40, -172, 64],
      ceiling: 0,
      floorColor: ASPHALT,
      floor: 'asphalt',
    },
    // The spur to the gatehouse at the junction, where the public road comes in.
    {
      id: 'gate',
      name: 'Gatehouse',
      zone: 'outside',
      rect: [-172, 64, -132, 110],
      ceiling: 0,
      floorColor: ASPHALT,
      floor: 'asphalt',
    },
    // The dry wash east of the road, where the burros come down from.
    {
      id: 'wash',
      name: 'Burro Wash',
      zone: 'outside',
      rect: [-148, 6, -100, 64],
      ceiling: 0,
      floorColor: 0x9c8a6c,
      floor: 'gravel',
    },
    /*
     * The Caytons, at the end of the road: the golf club next door, with its
     * clubhouse and lot, four courts north of the lot, and the course round it.
     */
    {
      id: 'caytons',
      name: 'The Caytons',
      zone: 'outside',
      rect: [-462, -76, -222, 184],
      ceiling: 0,
      floorColor: 0x54603f,
      floor: 'gravel',
    },
    {
      id: 'office',
      name: "Manager's Office",
      zone: 'restricted',
      rect: [48, -22, 58, -2],
      ceiling: 9,
      floorColor: OFFICE_FLOOR,
      floor: 'carpet',
      ceilingColor: CONCRETE_CEIL,
    },
    {
      id: 'break',
      name: 'Staff Break Room',
      zone: 'boh',
      rect: [58, -34, 94, -2],
      ceiling: 9,
      floorColor: BREAK_FLOOR,
      floor: 'vinyl',
      ceilingColor: CONCRETE_CEIL,
    },
    {
      id: 'alcove',
      name: 'Muster Alcove',
      zone: 'boh',
      rect: [94, -16, 104, -2],
      ceiling: 9,
      floorColor: CONCRETE_FLOOR,
      floor: 'concrete',
      ceilingColor: CONCRETE_CEIL,
    },
    {
      id: 'storage',
      name: 'Banquet Storage',
      zone: 'boh',
      rect: [104, -54, 176, -2],
      ceiling: 12,
      floorColor: STORE_FLOOR,
      floor: 'concrete',
      ceilingColor: CONCRETE_CEIL,
    },
    {
      id: 'ballroom',
      name: 'Crown Ballroom',
      zone: 'event',
      rect: [6, 10, 106, 102],
      ceiling: 24,
      floorColor: BALLROOM_FLOOR,
      floor: 'carpet',
      ceilingColor: BALLROOM_CEIL,
    },
    {
      id: 'lobby',
      name: 'Lobby',
      zone: 'foh',
      rect: [106, 10, 196, 78],
      ceiling: 20,
      floorColor: MARBLE_FLOOR,
      floor: 'marble',
      ceilingColor: MARBLE_CEIL,
    },
  ],

  walls: [
    // --- the corridor, north side -------------------------------------------
    // Split by what is on the far side, not by where the doors are. A wall
    // between two rooms has to be as tall as the *taller* of them, or you stand
    // in the fourteen foot loading dock and look over a nine foot wall into the
    // void above the corridor. Door openings are still declared as gaps in a
    // run of wall, measured along it from `from`, so moving a door is one
    // number.
    { id: 'corridor-n-far', from: [-62, -2], to: [-56, -2], height: 9, color: CONCRETE },
    {
      id: 'kitchen-s',
      from: [-56, -2],
      to: [-8, -2],
      height: 12,
      color: CONCRETE,
      // Six feet, because a cart of chairs comes back out through it.
      doors: [{ at: 22, width: 6, height: 8 }],
    },
    /*
     * The north half of the run's junction with the corridor.
     *
     * The first fourteen feet of this wall are not there: that is the run,
     * passing through on its way to the dock floor. It is declared as an
     * opening rather than by deleting the wall so the *header* survives — the
     * dock is fourteen feet tall and the corridor is nine, and a hole with no
     * header lets you stand on the dock and look over the corridor ceiling into
     * the void above it. Nine feet of opening is the whole corridor, which is
     * all a cart needs and more than a person does.
     */
    {
      id: 'dock-s',
      from: [-8, -2],
      to: [48, -2],
      height: 14,
      color: CONCRETE,
      doors: [
        { at: 0, width: 14, height: 9 },
        { at: 28, width: 6, height: 8 },
      ],
    },
    {
      id: 'office-s',
      from: [48, -2],
      to: [58, -2],
      height: 9,
      color: CONCRETE,
      doors: [{ at: 3, width: 3, height: 7 }],
    },
    {
      id: 'break-s',
      from: [58, -2],
      to: [94, -2],
      height: 9,
      color: CONCRETE,
      doors: [{ at: 12, width: 3, height: 7 }],
    },
    {
      id: 'alcove-s',
      from: [94, -2],
      to: [104, -2],
      height: 9,
      color: CONCRETE,
      // The whole ten foot face is the opening. What is left is the header, and
      // an alcove you can see into from down the corridor is the point: the
      // time clock has to be visible from far enough away to panic about.
      doors: [{ at: 0, width: 10, height: 8 }],
    },
    {
      id: 'storage-s',
      from: [104, -2],
      to: [176, -2],
      height: 12,
      color: CONCRETE,
      // Full corridor height, for carts and for a stack of banquet rounds.
      doors: [{ at: 26, width: 8, height: 9 }],
    },
    { id: 'corridor-n-e', from: [176, -2], to: [184, -2], height: 9, color: CONCRETE },

    // --- the corridor, south side -------------------------------------------
    { id: 'corridor-s-far', from: [-62, 10], to: [-56, 10], height: 9, color: CONCRETE },
    {
      id: 'shop-n',
      from: [-56, 10],
      to: [-8, 10],
      height: 11,
      color: CONCRETE,
      // Three feet. Engineering does not hand out goods, only permission, so
      // there is never a cart at this door — and a door the cart cannot use is
      // a door that says so.
      doors: [{ at: 22, width: 3, height: 7 }],
    },
    // The south half of the junction is simply absent. Both sides are nine feet,
    // so there is nothing above the opening to hold up and nothing to see over.
    // This is where the branch crosses the run.
    {
      id: 'ballroom-n',
      from: [6, 10],
      to: [106, 10],
      // Twenty-four feet, because that is the ballroom side. On the corridor
      // side it is capped at nine by the corridor's own ceiling.
      height: 24,
      color: BALLROOM_WALL,
      doors: [
        { at: 24, width: 6, height: 8 },
        { at: 72, width: 6, height: 8 },
      ],
    },
    {
      id: 'lobby-n',
      from: [106, 10],
      to: [184, 10],
      height: 20,
      color: MARBLE_WALL,
      // The lobby door. The mistake.
      doors: [{ at: 62, width: 3, height: 7 }],
    },
    { id: 'corridor-w', from: [-62, -2], to: [-62, 10], height: 9, color: CONCRETE },
    { id: 'corridor-e', from: [184, -2], to: [184, 10], height: 9, color: CONCRETE },

    // --- main kitchen --------------------------------------------------------
    { id: 'kitchen-w', from: [-56, -44], to: [-56, -2], height: 12, color: CONCRETE },
    // Also the loading dock's west wall now, so it takes the dock's fourteen
    // feet rather than the kitchen's twelve.
    { id: 'kitchen-e', from: [-8, -44], to: [-8, -2], height: 14, color: CONCRETE },
    { id: 'kitchen-n', from: [-56, -44], to: [-8, -44], height: 12, color: CONCRETE },

    // --- engineering shop ----------------------------------------------------
    { id: 'shop-w', from: [-56, 10], to: [-56, 44], height: 11, color: CONCRETE },
    { id: 'shop-e', from: [-8, 10], to: [-8, 44], height: 11, color: CONCRETE },
    { id: 'shop-s', from: [-56, 44], to: [-8, 44], height: 11, color: CONCRETE },

    // --- loading dock --------------------------------------------------------
    // Only the eight feet north of the kitchen: below that the kitchen's east
    // wall is the dock's west wall.
    { id: 'dock-w', from: [-8, -52], to: [-8, -44], height: 14, color: CONCRETE },
    // Also the manager's office west wall, for its southern twenty feet.
    { id: 'dock-e', from: [48, -52], to: [48, -2], height: 14, color: CONCRETE },
    // Storage is twelve feet and the alcove and break room are nine, so the
    // wall between them belongs to storage.
    {
      id: 'dock-n',
      from: [-8, -52],
      to: [48, -52],
      height: 14,
      color: CONCRETE,
      // Roll-up doors: full height, so no header. This is how the property
      // breathes — the only opening to outside. Both still land on the apron,
      // which starts at x=0; the eight feet west of it back onto the staff lot.
      doors: [
        { at: 14, width: 12, height: 14 },
        { at: 36, width: 12, height: 14 },
      ],
    },

    // --- dock apron, outside -------------------------------------------------
    // Eight feet of yard wall. Tall enough to hide the roofline of the rest of
    // the property from outside, short enough that the sky is still the loudest
    // thing out here.
    {
      id: 'apron-w',
      from: [0, -76],
      to: [0, -52],
      height: 8,
      color: 0x4b4e54,
      // The gate onto the staff lot. Wide, because vans come through it.
      doors: [{ at: 8, width: 10, height: 8 }],
    },
    /*
     * The lot, fenced on every side, with the one gate west onto the drive.
     *
     * `lot-e` is the one that is easy to forget. The apron's west wall is the
     * lot's east side only for the stretch the apron is long — thirty-six feet
     * of the lot reach further north than the apron does, and for that stretch
     * there was nothing there at all. The reachability test found it by
     * flooding the entire county.
     */
    { id: 'lot-e', from: [0, -112], to: [0, -76], height: 7, color: 0x4b4e54 },
    { id: 'lot-n', from: [-64, -112], to: [0, -112], height: 7, color: 0x4b4e54 },
    // Stops at x=-8, where the dock's own north wall takes over. Two walls in
    // the same line is the thing this file does not do.
    { id: 'lot-s', from: [-64, -52], to: [-8, -52], height: 7, color: 0x4b4e54 },
    {
      id: 'lot-w',
      from: [-64, -112],
      to: [-64, -52],
      height: 7,
      color: 0x4b4e54,
      // The lot gate, onto the West Drive. Twelve feet, which is a golf cart
      // and a cart behind it with a foot either side.
      doors: [{ at: 12, width: 12, height: 7 }],
    },

    // --- the West Drive and the lane -------------------------------------------
    // Low walls, stacked stone: you see over them and you do not get over them.
    // Past the west one is the guest parking; past the east one, the building.
    { id: 'drive-n', from: [-88, -112], to: [-64, -112], height: 7, color: 0x4b4e54 },
    {
      id: 'drive-w',
      from: [-88, -112],
      to: [-88, 126],
      height: 3.5,
      color: 0x8a7a64,
      // Where the cart road leaves it, west, behind the back of house.
      doors: [{ at: 76, width: 24 }],
    },
    { id: 'drive-s', from: [-88, 126], to: [-64, 126], height: 3.5, color: 0x8a7a64 },
    { id: 'drive-e', from: [-64, -52], to: [-64, 102], height: 4, color: 0x8a7a64 },
    { id: 'lane-n-w', from: [-64, 102], to: [-8, 102], height: 4, color: 0x8a7a64 },
    // (-8 to 106 is the building's own south wall: the run's end, then the ballroom.)
    { id: 'lane-n-e', from: [106, 102], to: [124, 102], height: 4, color: 0x8a7a64 },
    {
      id: 'lane-s',
      from: [-64, 126],
      to: [124, 126],
      height: 4,
      color: 0x8a7a64,
      // The Sage Lawn's gate.
      doors: [{ at: 114, width: 12 }],
    },

    // --- the lawns -------------------------------------------------------------
    // Clipped hedge, six feet: a lawn is a room with a sky for a ceiling.
    { id: 'sage-w', from: [16, 126], to: [16, 190], height: 6, color: 0x5d6b4c },
    { id: 'sage-s', from: [16, 190], to: [96, 190], height: 6, color: 0x5d6b4c },
    { id: 'sage-e', from: [96, 126], to: [96, 190], height: 6, color: 0x5d6b4c },
    {
      id: 'lawn-w',
      from: [124, 78],
      to: [124, 200],
      height: 6,
      color: 0x5d6b4c,
      // The Breeze Lawn's gate, off the end of the lane. Twelve feet, like the
      // lot's: a golf cart towing a cart through it squared up.
      doors: [{ at: 30, width: 12 }],
    },
    // (124 to 196 along its north side is the lobby's south wall.)
    {
      id: 'lawn-n',
      from: [196, 78],
      to: [280, 78],
      height: 6,
      color: 0x5d6b4c,
      // Through to the Wren Lawn, and the north-east gate onto the service
      // drive, twelve feet like the others.
      doors: [{ at: 18, width: 12 }, { at: 62, width: 12 }],
    },
    { id: 'lawn-e', from: [280, 78], to: [280, 200], height: 6, color: 0x5d6b4c },
    { id: 'lawn-s', from: [124, 200], to: [280, 200], height: 6, color: 0x5d6b4c },
    // (The Wren Lawn's west side is the lobby's east wall.)
    { id: 'wren-n', from: [196, 10], to: [252, 10], height: 6, color: 0x5d6b4c },
    {
      id: 'wren-e',
      from: [252, 10],
      to: [252, 78],
      height: 6,
      color: 0x5d6b4c,
      // A garden gate onto the service drive.
      doors: [{ at: 26, width: 6 }],
    },

    // --- the service drive -----------------------------------------------------
    // Painted block, seven feet, like the lot's: the back of a hotel.
    { id: 'service-n-n', from: [48, -76], to: [276, -76], height: 7, color: 0x4b4e54 },
    // (104 to 176 along its south side is banquet storage's north wall.)
    { id: 'service-n-s-w', from: [48, -54], to: [104, -54], height: 7, color: 0x4b4e54 },
    { id: 'service-n-s-e', from: [176, -54], to: [252, -54], height: 7, color: 0x4b4e54 },
    { id: 'service-e-w', from: [252, -54], to: [252, 10], height: 7, color: 0x4b4e54 },
    {
      id: 'service-e-e',
      from: [276, -76],
      to: [276, 78],
      height: 7,
      color: 0x4b4e54,
      // The pool's gate, onto its south deck.
      doors: [{ at: 46, width: 12 }],
    },

    // --- the pool --------------------------------------------------------------
    // A pool fence, five feet, round the deck. The gate is off the service drive.
    { id: 'pool-wall-s', from: [276, 10], to: [410, 10], height: 5, color: 0x6a6f78 },
    // (Its west side, along the service drive, is the drive's east wall.)
    { id: 'pool-wall-w', from: [276, -120], to: [276, -76], height: 5, color: 0x6a6f78 },
    { id: 'pool-wall-n', from: [276, -120], to: [410, -120], height: 5, color: 0x6a6f78 },
    { id: 'pool-wall-e', from: [410, -120], to: [410, 10], height: 5, color: 0x6a6f78 },

    // --- the cart road ---------------------------------------------------------
    // The same low stone walls, both sides, all the way. Past them is desert.
    { id: 'road-1-n', from: [-172, -36], to: [-88, -36], height: 3.5, color: 0x8a7a64 },
    { id: 'road-1-s', from: [-148, -12], to: [-88, -12], height: 3.5, color: 0x8a7a64 },
    { id: 'road-2-w', from: [-172, -36], to: [-172, 40], height: 3.5, color: 0x8a7a64 },
    {
      id: 'road-2-e',
      from: [-148, -12],
      to: [-148, 64],
      height: 3.5,
      color: 0x8a7a64,
      // Where the wash comes down to the road: a gap in the wall the width of
      // the wash's mouth, which is the burros' way out of it.
      doors: [{ at: 32, width: 30 }],
    },
    { id: 'road-3-n', from: [-222, 40], to: [-172, 40], height: 3.5, color: 0x8a7a64 },
    { id: 'road-3-s', from: [-222, 64], to: [-172, 64], height: 3.5, color: 0x8a7a64 },
    // The gatehouse spur. (Its north side, east of the road, is the wash's bank.)
    { id: 'gate-w', from: [-172, 64], to: [-172, 110], height: 3.5, color: 0x8a7a64 },
    { id: 'gate-e', from: [-132, 64], to: [-132, 110], height: 3.5, color: 0x8a7a64 },
    { id: 'gate-s', from: [-172, 110], to: [-132, 110], height: 3.5, color: 0x8a7a64 },
    // The wash's banks. (Its west side is the road's wall, with the mouth in it.)
    { id: 'wash-n', from: [-148, 6], to: [-100, 6], height: 6, color: 0x8e7462 },
    { id: 'wash-e', from: [-100, 6], to: [-100, 64], height: 6, color: 0x8e7462 },
    { id: 'wash-s', from: [-148, 64], to: [-100, 64], height: 6, color: 0x8e7462 },

    // --- the Caytons -----------------------------------------------------------
    // The club's own boundary, which is a boundary and not a wall.
    { id: 'caytons-n', from: [-462, -76], to: [-222, -76], height: 8, color: 0x6a705a },
    { id: 'caytons-w', from: [-462, -76], to: [-462, 184], height: 8, color: 0x6a705a },
    { id: 'caytons-s', from: [-462, 184], to: [-222, 184], height: 8, color: 0x6a705a },
    {
      id: 'caytons-e',
      from: [-222, -76],
      to: [-222, 184],
      height: 8,
      color: 0x6a705a,
      // Where the road comes in.
      doors: [{ at: 116, width: 24, height: 8 }],
    },
    {
      id: 'apron-e',
      from: [48, -76],
      to: [48, -52],
      height: 8,
      color: 0x4b4e54,
      // The service drive's gate. Twelve feet, for a golf cart and a cart on
      // the back, square to the apron and clear of its stairs.
      doors: [{ at: 2, width: 12, height: 8 }],
    },
    { id: 'apron-n', from: [0, -76], to: [48, -76], height: 8, color: 0x4b4e54 },

    // --- manager's office ----------------------------------------------------
    { id: 'office-n', from: [48, -22], to: [58, -22], height: 9, color: CONCRETE },

    // --- break room ----------------------------------------------------------
    { id: 'break-w', from: [58, -34], to: [58, -2], height: 9, color: CONCRETE },
    // Also the alcove's west wall.
    { id: 'break-e', from: [94, -34], to: [94, -2], height: 12, color: CONCRETE },
    { id: 'break-n', from: [58, -34], to: [94, -34], height: 9, color: CONCRETE },

    // --- muster alcove -------------------------------------------------------
    { id: 'alcove-n', from: [94, -16], to: [104, -16], height: 9, color: CONCRETE },

    // --- banquet storage -----------------------------------------------------
    // Also the alcove's east wall.
    { id: 'storage-w', from: [104, -54], to: [104, -2], height: 12, color: CONCRETE },
    { id: 'storage-e', from: [176, -54], to: [176, -2], height: 12, color: CONCRETE },
    { id: 'storage-n', from: [104, -54], to: [176, -54], height: 12, color: CONCRETE },

    // --- ballroom ------------------------------------------------------------
    /*
     * The ballroom service entrance, at the bottom of the main run.
     *
     * Twelve feet wide and ten tall, which is a loading door and not a doorway:
     * the run exists so a loaded cart can be brought the length of the property
     * without stopping, and a three foot door at the end of it would put the
     * stop back. It sits in the last twelve feet of the run, behind the stage,
     * which is where a banquet service entrance goes.
     */
    {
      id: 'ballroom-w',
      from: [6, 10],
      to: [6, 102],
      height: 24,
      color: BALLROOM_WALL,
      doors: [{ at: 76, width: 12, height: 10 }],
    },
    // The run's own two walls. Its west side is the engineering shop as far as
    // z=44 and open ground after that; its east side is the ballroom the whole
    // way, which is why only these two rows are needed.
    { id: 'run-w', from: [-8, 44], to: [-8, 102], height: 9, color: CONCRETE },
    { id: 'run-s', from: [-8, 102], to: [6, 102], height: 9, color: CONCRETE },
    { id: 'ballroom-s', from: [6, 102], to: [106, 102], height: 24, color: BALLROOM_WALL },
    {
      id: 'ballroom-e',
      from: [106, 10],
      to: [106, 102],
      height: 24,
      color: BALLROOM_WALL,
      // The grand entrance, from the lobby. Guests come in this way, which is
      // exactly why you may not.
      doors: [{ at: 20, width: 10, height: 12 }],
    },

    // --- lobby ---------------------------------------------------------------
    { id: 'lobby-n-e', from: [184, 10], to: [196, 10], height: 20, color: MARBLE_WALL },
    { id: 'lobby-e', from: [196, 10], to: [196, 78], height: 20, color: MARBLE_WALL },
    { id: 'lobby-s', from: [106, 78], to: [196, 78], height: 20, color: MARBLE_WALL },
    // The lobby's west wall below the ballroom doors, closing the corner.
    { id: 'lobby-w', from: [106, 78], to: [106, 102], height: 24, color: BALLROOM_WALL },
  ],

  props: [
    // --- loading dock --------------------------------------------------------
    // The dock is at truck bed height and the apron outside is at grade, which
    // is the whole reason a loading dock looks the way it does.
    { id: 'dock-platform', rect: [-8, -52, 48, -42], height: 4, color: 0x5a5e66 },
    // Five risers up to it. Each is under the controller's step height, so you
    // walk up rather than having to jump.
    { id: 'dock-step-1', rect: [12, -37.5, 22, -36], height: 0.8, color: 0x5a5e66 },
    { id: 'dock-step-2', rect: [12, -39, 22, -37.5], height: 1.6, color: 0x5a5e66 },
    { id: 'dock-step-3', rect: [12, -40.5, 22, -39], height: 2.4, color: 0x5a5e66 },
    { id: 'dock-step-4', rect: [12, -42, 22, -40.5], height: 3.2, color: 0x5a5e66 },
    // Stairs on the apron side, up through the second roll-up door. Without
    // them the dock doors are a one-way four foot drop and the apron is a
    // place you can get stranded, which turns a restricted area into a trap.
    { id: 'apron-step-1', rect: [30, -56, 38, -55], height: 0.8, color: 0x5a5e66 },
    { id: 'apron-step-2', rect: [30, -55, 38, -54], height: 1.6, color: 0x5a5e66 },
    { id: 'apron-step-3', rect: [30, -54, 38, -53], height: 2.4, color: 0x5a5e66 },
    { id: 'apron-step-4', rect: [30, -53, 38, -52], height: 3.2, color: 0x5a5e66 },
    /*
     * The receiving pallet, at the head of the main run.
     *
     * Empty most of the time. It is where anything that came off a truck is
     * put down, which is where `rounds-short` has always said its rounds are,
     * and it is eight feet from the top of the straight — so the fetch it
     * creates is the whole length of the property with a cart under it.
     *
     * Deliberately a pallet and not a stack: twelve inches high, so there is
     * still nothing beside the run to get behind.
     */
    { id: 'dock-pallet-a', rect: [6, -40, 11, -32], height: 1.2, color: 0x6f5f4a },
    { id: 'dock-pallet-b', rect: [10, -24, 16, -18], height: 2.2, color: 0x6f5f4a },
    { id: 'dock-crate', rect: [34, -34, 44, -24], height: 5, color: 0x66573f },
    { id: 'dock-bin', rect: [36, -18, 44, -10], height: 4, color: 0x3f5548 },
    // A smaller crate beside the big one. Nobody put it there to be climbed;
    // it is two and a half feet tall next to a five foot one, which makes it a
    // step onto the five foot one, and that is the whole of the arrangement.
    { id: 'dock-crate-small', rect: [26, -32, 32, -26], height: 2.5, color: 0x66573f },

    // --- the staff lot -------------------------------------------------------
    // Two rows of kerb and a skip. Nothing happens here, which is the point.
    { id: 'lot-kerb-a', rect: [-58, -104, -8, -103], height: 0.5, color: 0x6a6f78 },
    { id: 'lot-kerb-b', rect: [-58, -84, -8, -83], height: 0.5, color: 0x6a6f78 },
    { id: 'lot-skip', rect: [-60, -70, -52, -62], height: 5, color: 0x5a5548 },
    { id: 'lot-shelter', rect: [-30, -62, -18, -58], base: 7, height: 0.5, color: 0x5d636d },
    { id: 'lot-shelter-post-a', rect: [-30, -62, -29, -61], height: 7, color: 0x4a4f58 },
    { id: 'lot-shelter-post-b', rect: [-19, -62, -18, -61], height: 7, color: 0x4a4f58 },
    { id: 'lot-shelter-post-c', rect: [-30, -59, -29, -58], height: 7, color: 0x4a4f58 },
    { id: 'lot-shelter-post-d', rect: [-19, -59, -18, -58], height: 7, color: 0x4a4f58 },

    // --- the Breeze Lawn ------------------------------------------------------
    /*
     * What is left of the last function: the rounds folded and stood on edge
     * in a rack, and the chairs in stacks. South of the carts, which wait
     * between them and the north-east gate, and the walk from a stack to a
     * cart is the crew's job.
     */
    { id: 'lawn-rounds', rect: [232, 142, 240, 146], height: 3, color: 0x9a7a52 },
    { id: 'lawn-chairs', rect: [256, 142, 264, 146], height: 3.4, color: 0x4a6a8a },
    /*
     * And two long tables, set for the Friday dinner, in the lawn's north-west
     * quarter, a little under seventy feet from the gate: so a herd brought to
     * the gate smells the bread, and one anywhere up the lane short of its last
     * few yards does not. Out of the strike carts' way, which is north-east
     * to the service drive. Eight feet by four, the size a lawn dinner is set
     * on.
     */
    { id: 'lawn-table-a', rect: [178, 92, 182, 100], height: 2.5, color: 0xd8d0bc },
    { id: 'lawn-table-b', rect: [188, 92, 192, 100], height: 2.5, color: 0xd8d0bc },

    // --- the Sunstone Pool, and the Saltbush beside it --------------------------
    // The restaurant is a building on the deck's north side; its terrace is the
    // deck in front of it.
    { id: 'saltbush', rect: [356, -120, 410, -98], height: 14, color: 0xb89a78 },
    // The pool's walls, below the deck, so the water has sides.
    { id: 'pool-side-n', rect: [300, -80.5, 370, -80], base: -3.5, height: 3.5, color: 0x9ab8c0 },
    { id: 'pool-side-s', rect: [300, -40, 370, -39.5], base: -3.5, height: 3.5, color: 0x9ab8c0 },
    { id: 'pool-side-w', rect: [299.5, -80, 300, -40], base: -3.5, height: 3.5, color: 0x9ab8c0 },
    { id: 'pool-side-e', rect: [370, -80, 370.5, -40], base: -3.5, height: 3.5, color: 0x9ab8c0 },
    // Loungers along the west deck, and the bar's counter at the restaurant.
    { id: 'pool-lounger-a', rect: [280, -76, 286, -73], height: 1.3, color: 0xe8e2d4 },
    { id: 'pool-lounger-b', rect: [280, -66, 286, -63], height: 1.3, color: 0xe8e2d4 },
    { id: 'pool-lounger-c', rect: [280, -56, 286, -53], height: 1.3, color: 0xe8e2d4 },
    { id: 'saltbush-bar', rect: [364, -96, 396, -93], height: 3.5, color: 0x5a3d28 },

    // --- speed bumps on the cart road --------------------------------------------
    /*
     * Four inches of asphalt across the road, two on the straight west from
     * the drive and one on the straight to the Caytons. A golf cart takes
     * them without noticing; whatever it is towing on castors does not
     * (`wheeled.js`, BUMP). Across the whole width, because a bump you can
     * drive round is a suggestion.
     */
    { id: 'road-bump-a', rect: [-132, -36, -130.5, -12], height: 0.3, color: 0x6a6f78 },
    { id: 'road-bump-b', rect: [-110, -36, -108.5, -12], height: 0.3, color: 0x6a6f78 },
    { id: 'road-bump-c', rect: [-200, 40, -198.5, 64], height: 0.3, color: 0x6a6f78 },

    // --- the gatehouse --------------------------------------------------------
    // The guard's hut at the junction, and the boom across the way out. The
    // boom is down: the public road is past it, and this game is not.
    { id: 'gatehouse', rect: [-144, 72, -134, 88], height: 9, color: 0xb89a78 },
    { id: 'gate-boom', rect: [-172, 100, -144, 100.5], base: 3, height: 0.4, color: 0xd8d0bc },

    // --- the Caytons ---------------------------------------------------------
    /*
     * The clubhouse, and its lot east of it. The terrace in front of the
     * clubhouse is where the club seats a dinner.
     */
    { id: 'clubhouse', rect: [-452, 108, -372, 174], height: 18, color: 0xb89a78 },
    { id: 'caytons-table-a', rect: [-428, 84, -420, 88], height: 2.5, color: 0xd8d0bc },
    { id: 'caytons-table-b', rect: [-408, 84, -400, 88], height: 2.5, color: 0xd8d0bc },
    { id: 'caytons-kerb-a', rect: [-352, 118, -254, 119], height: 0.5, color: 0x6a6f78 },
    { id: 'caytons-kerb-b', rect: [-352, 148, -254, 149], height: 0.5, color: 0x6a6f78 },
    /*
     * Four courts, north of the lot, in one fence: two by two, each with its
     * net. There is no game here and there is not going to be one; the courts
     * are a place with fences you can climb, benches you can stand on, and a
     * hard surface that carries sound. Whatever happens on them is whatever
     * you brought.
     */
    { id: 'court-a-net', rect: [-348, -9, -326, -8.6], base: 1.5, height: 1.5, color: 0x3c4a3c },
    { id: 'court-b-net', rect: [-298, -9, -276, -8.6], base: 1.5, height: 1.5, color: 0x3c4a3c },
    { id: 'court-c-net', rect: [-348, 46, -326, 46.4], base: 1.5, height: 1.5, color: 0x3c4a3c },
    { id: 'court-d-net', rect: [-298, 46, -276, 46.4], base: 1.5, height: 1.5, color: 0x3c4a3c },
    { id: 'court-fence-n', rect: [-362, -36, -262, -35.5], height: 10, color: 0x5a6068 },
    { id: 'court-fence-w', rect: [-362, -36, -361.5, 74], height: 10, color: 0x5a6068 },
    { id: 'court-fence-e', rect: [-262.5, -36, -262, 74], height: 10, color: 0x5a6068 },
    // Two lengths of fence with the gate between them, facing the lot, because
    // a court you cannot get onto is a wall with a net behind it.
    { id: 'court-fence-s-w', rect: [-362, 73.5, -318, 74], height: 10, color: 0x5a6068 },
    { id: 'court-fence-s-e', rect: [-306, 73.5, -262, 74], height: 10, color: 0x5a6068 },
    { id: 'court-bench-a', rect: [-258, -16, -256, -8], height: 1.5, color: 0x6a6252 },
    { id: 'court-bench-b', rect: [-258, 34, -256, 42], height: 1.5, color: 0x6a6252 },

    /*
     * The edge of the course, and three mesquites along it.
     *
     * A tree is a trunk and a canopy, and it is climbable for the same reason
     * a stack of crates is: the canopy's underside is a top at a height, and
     * there is room for a body on it. Nobody wrote a climbing point into a
     * tree and nobody is going to.
     */
    { id: 'green-edge', rect: [-460, -72, -442, 100], height: 0.4, color: 0x60724a },
    /*
     * Each one is a trunk, a low fork, and a canopy that leans away from the
     * fork. The lean is not decoration: a canopy directly over the fork is a
     * ceiling four feet above it, and the climbing rule refuses a top with no
     * room for a body on it — correctly, and it makes the tree furniture.
     */
    { id: 'tree-a-trunk', rect: [-436, -52, -433, -49], height: 8, color: 0x4a3f34 },
    { id: 'tree-a-canopy', rect: [-444, -58, -434, -44], base: 8, height: 3, color: 0x455c38 },
    { id: 'tree-a-fork', rect: [-432, -51, -429, -48], height: 4, color: 0x4a3f34 },
    { id: 'tree-b-trunk', rect: [-436, -2, -433, 1], height: 7, color: 0x4a3f34 },
    { id: 'tree-b-canopy', rect: [-444, -8, -434, 6], base: 7, height: 3, color: 0x455c38 },
    { id: 'tree-b-fork', rect: [-432, -1, -429, 2], height: 3.5, color: 0x4a3f34 },
    { id: 'tree-c-trunk', rect: [-436, 48, -433, 51], height: 9, color: 0x4a3f34 },
    { id: 'tree-c-canopy', rect: [-444, 42, -434, 56], base: 9, height: 3, color: 0x455c38 },
    { id: 'tree-c-fork', rect: [-432, 49, -429, 52], height: 4.5, color: 0x4a3f34 },
    // The club's cart path, from the terrace up the west side of the courts to
    // the strip north of them, where it turns out onto the course.
    { id: 'cart-path', rect: [-382, -64, -376, 104], height: 0.2, color: 0x7a7060 },
    /*
     * A row of palo verde along the north side, planted where the Caytons'
     * own carts actually go: the open strip between the boundary and the
     * court fence, which is the only way round the courts that a cart fits.
     * Three trunks, which are solid like any trunk; their low branches are in
     * `swipes`, because a branch is not something a cart runs into — it is
     * something the person on the back of it runs into.
     */
    { id: 'verde-a-trunk', rect: [-343, -74.5, -341.5, -73], height: 7, color: 0x6b7a4a },
    { id: 'verde-b-trunk', rect: [-313, -74.5, -311.5, -73], height: 7, color: 0x6b7a4a },
    { id: 'verde-c-trunk', rect: [-283, -74.5, -281.5, -73], height: 7, color: 0x6b7a4a },
    // The palo verde at the road's first bend, rooted just past its outside corner.
    { id: 'verde-road-trunk', rect: [-177, -41, -175.5, -39.5], height: 7, color: 0x6b7a4a },
    // The rail along the dock edge, which is there because a four foot drop
    // with forklifts working below it needs a rail.
    { id: 'dock-rail', rect: [-8, -52.6, 28, -52], base: 4, height: 3.2, color: 0x6a6f78 },
    // Two lifts of scaffold on the apron, where Engineering are still working
    // on the canopy. A scaffold is stacked boxes; so is everything else.
    { id: 'scaffold-lift-1', rect: [6, -70, 14, -67], height: 4, color: 0x7b7f86 },
    { id: 'scaffold-lift-2', rect: [6, -67, 14, -64], height: 7, color: 0x7b7f86 },

    // --- main kitchen --------------------------------------------------------
    // The pass: a long counter you talk across. Everything Kitchen ever gives
    // you comes over this, including the chairs they borrowed for a staff meal.
    { id: 'kitchen-pass', rect: [-24, -16, -12, -8], height: 3.5, color: 0x8d949c },
    { id: 'kitchen-range-a', rect: [-52, -40, -32, -34], height: 3, color: 0x6c737b },
    { id: 'kitchen-range-b', rect: [-52, -30, -32, -24], height: 3, color: 0x6c737b },
    { id: 'kitchen-rack', rect: [-52, -18, -46, -6], height: 7, color: 0x5a5f68 },
    // The stack Kitchen borrowed, behind the pass on their side of it, which is
    // where the sheet has always said it was. Out of the Chef's lane along
    // z=-6 — a prop standing on somebody's route is a person walking through a
    // stack of chairs.
    { id: 'kitchen-chairs', rect: [-24, -22, -16, -18], height: 3.4, color: 0x4a6a8a },
    /*
     * The walk-in, which is a room rather than a block.
     *
     * It was one solid box, because nothing needed to go in it. Four walls and
     * a doorway costs four more rows and means the inside of it exists — and
     * the inside of a cold room is somewhere you can stand where nobody can see
     * you, which the game now has a use for. Nothing about this says "hiding
     * place"; it says the walk-in has an inside, which walk-ins do.
     */
    { id: 'kitchen-walkin-w', rect: [-30, -42, -29, -32], height: 9, color: 0x59636b },
    { id: 'kitchen-walkin-e', rect: [-15, -42, -14, -32], height: 9, color: 0x59636b },
    { id: 'kitchen-walkin-n', rect: [-30, -42, -14, -41], height: 9, color: 0x59636b },
    { id: 'kitchen-walkin-s-w', rect: [-30, -33, -23.5, -32], height: 9, color: 0x59636b },
    { id: 'kitchen-walkin-s-e', rect: [-20, -33, -14, -32], height: 9, color: 0x59636b },
    { id: 'kitchen-walkin-header', rect: [-23.5, -33, -20, -32], base: 6.5, height: 2.5, color: 0x59636b },

    // --- engineering shop ----------------------------------------------------
    // Narrowed off the east wall so there is a clear lane from the shop door
    // to the back of the room. A bench across the only walkable line is a room
    // you can enter and not cross.
    { id: 'shop-bench', rect: [-50, 16, -38, 22], height: 3, color: 0x6a6252 },
    { id: 'shop-shelves', rect: [-54, 28, -42, 32], height: 7, color: 0x5a5f68 },
    { id: 'shop-risers', rect: [-30, 30, -16, 38], height: 2, color: 0x7a6a4a },
    { id: 'shop-gangbox', rect: [-30, 14, -20, 20], height: 3.5, color: 0x3f5548 },

    // --- housekeeping carts, parked in the corridor ---------------------------
    // Housekeeping owns the back corridor in practice, and this is what that
    // looks like: three carts nobody else is allowed to move.
    // Parked along the *south* wall. On the north side they stood squarely in
    // front of the manager's office door, which the connectivity test noticed
    // and nobody looking at it would have.
    { id: 'hskp-cart-a', rect: [40, 6.2, 44, 9.2], height: 3.5, color: 0x3f7f72 },
    { id: 'hskp-cart-b', rect: [46, 6.2, 50, 9.2], height: 3.5, color: 0x3f7f72 },
    { id: 'hskp-cart-c', rect: [52, 6.2, 56, 9.2], height: 3.5, color: 0x3f7f72 },

    /*
     * --- staged in the branch ------------------------------------------------
     *
     * What a department that thinks well enough of you does with the thing you
     * asked for: walks it round to the corridor and leaves it half way to the
     * room. Four of them, all against the north wall between the doors, none of
     * them tall and none of them a cart — a cart parked in a corridor is
     * somewhere to hide, and being owed a favour should not buy you cover.
     *
     * Spaced off each other and off every doorway on that wall: the dock door
     * is x=20..26, the office x=51..54, the break room x=70..73, the alcove
     * x=94..104 and storage x=130..138. Three feet deep rather than four,
     * because the corridor's walking line is z=4 and a body is nearly two feet
     * across — a foot of clearance is a scrape, and the Banquet Manager patrols
     * that line end to end.
     */
    { id: 'branch-rounds', rect: [34, -1, 40, 2], height: 1.2, color: 0x6f5f4a },
    { id: 'branch-chairs', rect: [44, -1, 50, 2], height: 3.4, color: 0x4a6a8a },
    { id: 'branch-risers', rect: [56, -1, 62, 2], height: 2, color: 0x7a6a4a },
    // Front Desk's goes east of the alcove, because Front Desk is east. Off the
    // marble is most of what that one buys.
    { id: 'branch-signage', rect: [106, -1, 112, 2], height: 3, color: 0x3c4658 },

    // --- manager's office ----------------------------------------------------
    { id: 'office-desk', rect: [50, -20, 56, -16], height: 2.5, color: 0x5b4a3b },
    { id: 'office-cabinet', rect: [50, -15, 52, -8], height: 5, color: 0x4d5258 },

    // --- break room ----------------------------------------------------------
    { id: 'break-lockers', rect: [59, -33, 80, -30], height: 6, color: 0x4f5a63 },
    { id: 'break-table-a', rect: [64, -26, 72, -20], height: 2.5, color: 0x6b6152 },
    { id: 'break-bench-a', rect: [64, -28, 72, -27], height: 1.4, color: 0x5d5446 },
    { id: 'break-bench-b', rect: [64, -19, 72, -18], height: 1.4, color: 0x5d5446 },
    { id: 'break-vending', rect: [90, -33, 93, -28], height: 6.5, color: 0x53484f },
    /*
     * The ice machine, next to the vending machine, which is where every hotel
     * in the world puts one. The only thing the prank pass adds to the
     * building: everything else it needs was already standing somewhere.
     */
    { id: 'break-ice', rect: [84, -33, 88, -29], height: 5.5, color: 0x6a757e },
    /*
     * And the coffee station beside it, because a sugar caddy has to be
     * somewhere for anybody to put salt in one. The second and last thing this
     * pass adds to the building.
     */
    { id: 'break-coffee', rect: [79, -33, 83, -30], height: 3, color: 0x6b6152 },
    // The cot. Ending the day is a thing you do to an object, not to a menu.
    { id: 'break-cot', rect: [86, -14, 92, -8], height: 1.6, color: 0x4a5560 },

    // --- muster alcove -------------------------------------------------------
    // Both wall-mounted, at reading height. The two objects the whole shift
    // loop turns on, deliberately in the same ten-foot alcove.
    { id: 'time-clock', rect: [94.3, -12, 95.3, -9], base: 3.2, height: 1.6, color: 0x9ea7b2 },
    { id: 'beo-board', rect: [102.7, -13, 103.7, -6], base: 3, height: 4, color: 0x2f4a3c },

    // --- banquet storage -----------------------------------------------------
    // Racking, in rows, so the room is a place you move through rather than a
    // cupboard you stand in front of.
    { id: 'rack-a', rect: [110, -50, 140, -46], height: 8, color: 0x5a5f68 },
    { id: 'rack-b', rect: [110, -40, 140, -36], height: 8, color: 0x5a5f68 },
    { id: 'rack-c', rect: [148, -50, 172, -46], height: 8, color: 0x5a5f68 },
    { id: 'rack-d', rect: [148, -40, 172, -36], height: 8, color: 0x5a5f68 },
    // The three supply points. Colour-coded because you will be sprinting at
    // them with fourteen minutes left.
    { id: 'store-rounds', rect: [110, -28, 122, -16], height: 2.6, color: 0x8a6a4a },
    { id: 'store-chairs', rect: [130, -28, 142, -16], height: 3.4, color: 0x4a6a8a },
    { id: 'store-linen', rect: [150, -28, 162, -16], height: 3.0, color: 0x8a4a6a },

    // --- ballroom ------------------------------------------------------------
    // Empty, on purpose. The only permanent things are the four columns that
    // tell you how big it is, and a flat pad you cannot set tables on.
    // Pulled in tight to the side walls so they are out of the setup grid. A
    // column standing in an anchor is a table you cannot set and a bug report
    // you will not understand for a week.
    /*
     * The stage, at the far end where a stage goes. A deck on legs, because
     * that is what a banquet stage is — four sections of staging on adjustable
     * feet — and because a deck on legs has an underneath.
     */
    { id: 'stage-deck', rect: [30, 94, 82, 101], base: 3.5, height: 0.6, color: 0x4f463c },
    { id: 'stage-leg-a', rect: [31, 95, 33, 97], height: 3.5, color: 0x3c352e },
    { id: 'stage-leg-b', rect: [79, 95, 81, 97], height: 3.5, color: 0x3c352e },
    { id: 'stage-leg-c', rect: [31, 98, 33, 100], height: 3.5, color: 0x3c352e },
    { id: 'stage-leg-d', rect: [79, 98, 81, 100], height: 3.5, color: 0x3c352e },

    /*
     * Housekeeping's half way, which is further than anybody else's because
     * theirs was never far. A cage of linen wheeled in through the near service
     * doors and left inside the room, north of the setting grid, which starts
     * at z=28 and wants six feet of aisle in front of it.
     */
    // West of the doors themselves: the Banquet Captain walks x=81 from the
    // corridor into the room on every lap, and a cage in that line is a person
    // walking through a cage.
    { id: 'ballroom-linen-cage', rect: [66, 14, 72, 18], height: 3.5, color: 0x8a8274 },

    /*
     * The air walls: the folding partitions that split the ballroom into
     * sections, stored in a pocket at each side wall when the room is one room.
     *
     * A pocket is the panels stacked flat against the wall at one end, a
     * pocket door across the front of it, and a gap between the end of the
     * stack and the end of the door where the next panels would go — which a
     * person can stand in. Two boxes each, both solid, full height to the
     * track. The gap is in `hollows.js` like the space under the stage: a
     * place with room in it, and the concealment rule is interested in that
     * whoever is standing there. The way in is the open end of the pocket.
     */
    { id: 'airwall-east-stack', rect: [101, 46, 105.6, 51], height: 10, color: 0x8b8071 },
    { id: 'airwall-east-door', rect: [100.5, 46, 101, 56], height: 10, color: 0x8b8071 },
    { id: 'airwall-west-stack', rect: [6.4, 40, 11, 45], height: 10, color: 0x8b8071 },
    { id: 'airwall-west-door', rect: [11, 40, 11.5, 50], height: 10, color: 0x8b8071 },

    { id: 'col-nw', rect: [12, 26, 16, 30], height: 24, color: BALLROOM_WALL },
    { id: 'col-ne', rect: [96, 26, 100, 30], height: 24, color: BALLROOM_WALL },
    { id: 'col-sw', rect: [12, 82, 16, 86], height: 24, color: BALLROOM_WALL },
    { id: 'col-se', rect: [96, 82, 100, 86], height: 24, color: BALLROOM_WALL },
    {
      id: 'dance-floor',
      rect: [40, 60, 72, 92],
      height: 0.08,
      color: 0x8d7b5e,
      solid: false,
    },

    // --- lobby ---------------------------------------------------------------
    // Against the east wall, facing the room. It used to sit across the top of
    // the lobby, which put it squarely on the manager's patrol route — the
    // route test found him walking through his own front desk.
    { id: 'lobby-desk', rect: [178, 30, 192, 62], height: 3.5, color: 0x8e8478 },
    { id: 'lobby-col-a', rect: [122, 30, 127, 35], height: 20, color: MARBLE_WALL },
    { id: 'lobby-col-b', rect: [122, 54, 127, 59], height: 20, color: MARBLE_WALL },
    { id: 'lobby-col-c', rect: [170, 30, 175, 35], height: 20, color: MARBLE_WALL },
    { id: 'lobby-col-d', rect: [170, 54, 175, 59], height: 20, color: MARBLE_WALL },
    // The signage board and its box of letters, stood in front of the desk. On
    // the marble, which is the entire cost of the shortfall that names it.
    { id: 'lobby-signboard', rect: [174, 44, 177, 48], height: 4, color: 0x3c4658 },
    { id: 'lobby-seat-a', rect: [136, 46, 148, 54], height: 1.5, color: 0x7d6a5c },
    { id: 'lobby-seat-b', rect: [152, 46, 164, 54], height: 1.5, color: 0x7d6a5c },
    // The rope at the service door. Waist high, walk straight over it, that is
    // the point — the barrier is policy, not geometry.
    {
      id: 'service-rope',
      rect: [166, 11.5, 173, 12.5],
      height: 2.8,
      color: 0x8c2f3a,
      solid: false,
    },
  ],

  /**
   * Volumes the rules care about. Checked every frame against the player, so
   * they are kept few and rectangular.
   *
   * `foh` is the lobby, and it is bigger than the lobby's floor on purpose:
   * the trigger starts inside the service doorway, so stepping through the door
   * is already the offence, which is how it reads to a manager standing in the
   * marble watching a banquet server appear.
   */
  /*
   * What is planted at rider height.
   *
   * Not props: nothing here stops anything. A walking person pushes through a
   * bush, a cart passes under a branch — the cart's body is lower than both.
   * What does not pass under them is somebody sitting on the cart, whose head
   * and shoulders are exactly where a branch that has grown out over a path
   * is. `wheeled.js` reads these and nothing else does.
   *
   *   rect     feet, like everything in this file
   *   base     feet off the ground where the growth starts
   *   height   feet of it
   *   kind     what it is, for whoever hears it
   */
  swipes: [
    // Each palo verde reaches out over the strip, at the height of somebody
    // sitting down and below the roof of anything with one.
    { id: 'verde-a-branch', rect: [-347, -73, -337, -57], base: 3.9, height: 1.9, kind: 'branch' },
    { id: 'verde-b-branch', rect: [-317, -73, -307, -57], base: 3.9, height: 1.9, kind: 'branch' },
    { id: 'verde-c-branch', rect: [-287, -73, -277, -57], base: 3.9, height: 1.9, kind: 'branch' },
    // Creosote along the court fence, grown up through the chain link and
    // out over the strip on the other side.
    { id: 'creosote-a', rect: [-332, -39.5, -326, -36], base: 1.4, height: 3.2, kind: 'bush' },
    { id: 'creosote-b', rect: [-302, -40, -296, -36], base: 1.4, height: 3.2, kind: 'bush' },
    /*
     * And the road's own: a palo verde leaning over the road's first bend, and
     * creosote grown out over the wall by the wash and the straight west. The
     * road is a road through the desert, and the desert is at its edges.
     */
    { id: 'verde-road-branch', rect: [-172, -36, -161, -27], base: 3.9, height: 1.9, kind: 'branch' },
    { id: 'road-creosote-a', rect: [-154, 52, -148, 62], base: 1.4, height: 3.2, kind: 'bush' },
    { id: 'road-creosote-b', rect: [-214, 40, -202, 46], base: 1.4, height: 3.2, kind: 'bush' },
  ],

  zones: [
    { id: 'foh-lobby', kind: 'foh', rect: [106, 9, 196, 78], label: 'Front of House' },
    { id: 'restricted-office', kind: 'restricted', rect: [48, -22, 58, -2], label: "Manager's Office" },
    { id: 'restricted-apron', kind: 'restricted', rect: [0, -76, 48, -52], label: 'Dock Apron' },
    { id: 'ballroom', kind: 'event', rect: [6, 10, 106, 102], label: 'Crown Ballroom' },
    { id: 'clock-zone', kind: 'muster', rect: [94, -16, 104, -2], label: 'Muster Alcove' },
    /*
     * The guest restrooms. Restricted, and there is no standing that opens
     * them — Management thinking the world of you does not make you a guest.
     * The one door in the building that is shut to you on the last day of the
     * game exactly as it was on the first.
     */
    { id: 'guest-restrooms', kind: 'restricted', rect: [180, 62, 196, 78], label: 'Guest Restrooms' },
    /*
     * The Caytons is restricted, and only on the clock.
     *
     * Which needed nothing building: conduct is only offered to perception
     * while you are on the clock, so a restricted zone out here is somewhere
     * you should not be during your shift and somewhere you are welcome to
     * stand around the rest of the time. The staff lot is not restricted at
     * all — it is just quiet.
     */
    { id: 'caytons-zone', kind: 'restricted', rect: [-462, -76, -222, 184], label: 'The Caytons' },
    /*
     * The pool's south deck, which is where an event is set out there. An
     * event room like the ballroom: what is drawn for a room and put down in
     * one counts as in it, whichever one.
     */
    { id: 'pool-deck', kind: 'event', rect: [276, -40, 410, 10], label: 'Sunstone Pool' },
  ],

  /**
   * Things that are only solid sometimes.
   *
   * A barrier is a box the game can switch on and off — a chained door, a
   * blocked run. They are declared here with everything else rather than
   * conjured at runtime, so the blockout stays one file you can read, and so a
   * connectivity test can ask what the property looks like with a given one
   * closed.
   *
   * The near ballroom service door is the only one so far, and it is the whole
   * point of there being two: chain it and the walk from storage to the room
   * goes the long way round, past the far door at x=33. That is a real cost in
   * minutes, paid by geometry rather than by a number.
   */
  barriers: [
    /*
     * The walk-in door. Kitchen's cold room, and Kitchen decide who goes in it.
     *
     * Deliberately not on anybody's critical path — a keyed door across the
     * route the job actually runs on is not access, it is a toll. The first
     * pass put the threshold on the near ballroom doors and the clean run went
     * from a hundred and thirteen minutes to a hundred and forty-two, on day
     * one, before the player had done anything to deserve it.
     */
    {
      id: 'walkin-door',
      label: 'Walk-in locked',
      rect: [-23.5, -33, -20, -32],
      height: 7,
      color: 0x59636b,
    },
    {
      id: 'ballroom-door-east',
      label: 'Near ballroom doors chained',
      rect: [78, 9.4, 84, 10.6],
      height: 8,
      color: 0x8c2f3a,
    },
  ],

  /*
   * The ways across the site, as centrelines in feet: what a person driving
   * the golf cart out of the staff lot follows to get somewhere. Nothing in
   * the game steers by these; they are here so the headless scripts, the
   * minimap and a reader of this file agree on where the road goes.
   */
  routes: {
    // Out of the lot by its gate and onto the drive.
    lot: [[-40, -80], [-40, -94], [-60, -94], [-76, -94]],
    // Down the drive, along the lane, and in at the Breeze Lawn's gate.
    breeze: [[-76, -94], [-76, 114], [118, 114], [134, 114]],
    // On from the Breeze Lawn by its north-east gate, up the service drive,
    // and in at the pool's gate.
    pool: [[134, 114], [200, 112], [264, 92], [264, 70], [264, -24], [292, -24]],
    // The service drive, from the apron's east gate to the Breeze Lawn's
    // north-east gate: the short way from the lawns to the dock.
    service: [[20, -64], [60, -66], [250, -66], [264, -56], [264, 70], [264, 90]],
    // Down the drive, west onto the cart road, and on to the Caytons' lot:
    // round the first bend on its inside, under the palo verde's reach rather
    // than through it, and down the west half of the road past the wash, which
    // is round the snake lying across the middle of it rather than over it.
    caytons: [[-76, -94], [-76, -32], [-100, -24], [-158, -24], [-166, -14], [-166, 52], [-182, 52], [-234, 52], [-242, 84]],
    // From the lot to the apron gate and onto the apron.
    apron: [[-40, -80], [-20, -65], [-6, -63], [20, -60]],
  },

  /*
   * What stands round the property and is never reached: drawn, not solid.
   *
   * The hotel's own main building is a Y of guest wings north of the building
   * this game happens in, and the drive loops in to its front from the west;
   * the guest parking is west and south-west; there are residences round a
   * small lake down the road, and fairways round the Caytons. None of it is in
   * `levelSolids`: the look layer draws it and nothing else reads it. Feet,
   * like everything here. A mass is a box centred on `at`, `size` wide (x)
   * and deep (z) before it is turned by `yaw` degrees, `height` tall.
   */
  scenery: {
    masses: [
      // The main building: the hub, the south wing that meets the back of
      // house, and the two wings north-west and north-east.
      { id: 'hotel-hub', kind: 'hotel', at: [140, -186], size: [80, 80], yaw: 0, height: 64 },
      { id: 'hotel-wing-s', kind: 'hotel', at: [140, -118], size: [62, 68], yaw: 0, height: 52 },
      // And the bridge over the service drive that joins it to the back of house.
      { id: 'hotel-link', kind: 'bridge', at: [140, -68], size: [30, 30], yaw: 0, height: 26, base: 14 },
      { id: 'hotel-wing-nw', kind: 'hotel', at: [52, -262], size: [58, 170], yaw: 60, height: 58 },
      { id: 'hotel-wing-ne', kind: 'hotel', at: [228, -262], size: [58, 170], yaw: -60, height: 58 },
      // The porte-cochère on the hub's west face, over the end of the drive.
      { id: 'porte-cochere', kind: 'canopy', at: [80, -186], size: [36, 44], yaw: 0, height: 18 },
      // The residences round the lake, down the road.
      { id: 'villa-a', kind: 'villa', at: [-262, 246], size: [44, 30], yaw: 8, height: 16 },
      { id: 'villa-b', kind: 'villa', at: [-336, 246], size: [44, 30], yaw: -6, height: 16 },
      { id: 'villa-c', kind: 'villa', at: [-376, 300], size: [30, 44], yaw: 4, height: 16 },
      { id: 'villa-d', kind: 'villa', at: [-336, 354], size: [44, 30], yaw: 12, height: 16 },
      { id: 'villa-e', kind: 'villa', at: [-262, 354], size: [44, 30], yaw: -10, height: 16 },
    ],
    // Paved ground: the entry drive and its loop, and the guest parking.
    paved: [
      { id: 'entry-drive', rect: [-280, -196, 50, -176] },
      { id: 'entry-loop', circle: [40, -186, 40] },
      { id: 'guest-parking-w', rect: [-230, -100, -104, -44], stalls: true },
      { id: 'guest-parking-sw', rect: [-150, 130, -40, 230], stalls: true },
      { id: 'public-road', rect: [-168, 110, -136, 320] },
    ],
    water: [{ id: 'lake', ellipse: [-300, 300, 44, 24] }],
    fairways: [
      { id: 'fairway-w', rect: [-682, -96, -482, 244] },
      { id: 'fairway-n', rect: [-482, -256, -222, -96] },
      { id: 'green-s', ellipse: [-470, 236, 46, 28] },
    ],
  },

  /*
   * The pool deck's anchor grid, the ballroom's rules on a smaller floor: twelve
   * foot centres, three rows between the water and the fence. The row nearest
   * the water is the keep-clear, which is the pool's dance floor: legal to set
   * on, always the mistake, because the guests walk the edge.
   */
  poolDeck: {
    origin: [288, -28],
    spacing: 12,
    cols: 10,
    rows: 3,
    keepClear: [276, -34, 410, -22],
  },

  /** Where the cart is parked at the start of a run: banquet storage, by the door. */
  cartHome: { x: 126, z: -8 },

  /**
   * Where a banquet setup is allowed to go.
   *
   * A grid of anchors, because banquet rounds are set on a grid — a room diagram
   * is literally graph paper. Twelve foot centres is the real number for a
   * 72 inch round with chairs: six feet of table, six feet of aisle and chair
   * back. The keep-clear is the dance floor; setting a table on it is the
   * mistake a first-week banquet server actually makes.
   */
  ballroom: {
    origin: [26, 28],
    spacing: 12,
    cols: 6,
    rows: 6,
    /**
     * The dance floor. Six of the thirty-six anchors sit on it, and a table on
     * any of them is the mistake a first-week banquet server actually makes —
     * the grid does not stop at the dance floor, the diagram does.
     */
    keepClear: [40, 60, 72, 92],
  },
};

export default LEVEL;
